import {
    CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import sha256 from 'crypto-js/sha256';
import { Field, Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import api from '../api/api';
import { ReactComponent as Logo } from '../assets/logo.svg';
import useStore from '../store/store';

const Schema = Yup.object().shape({
    iso2: Yup.string().required('Required'),
    phone: Yup.string()
        .required('Required')
        .matches(/^[\d\s]+X?$/, 'Invalid phone number!'),
    password: Yup.string().required('Required'),
    passwordConfirmation: Yup.string()
        .required('Required')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    const { countries, formError, loading, setFormError, setLoading } =
        useStore((state) => state);
    const iso2 = localStorage.getItem('iso2') || 'AE';
    const password = '';
    const passwordConfirmation = '';
    const phone = '';
    const classes = useStyles();

    const ChangePassword = async ({ iso2, phone, password }) => {
        setLoading(true);

        try {
            const response = await api.post('/security/change-password', {
                iso2,
                phone,
                password: sha256(password).toString(),
            });

            const { success, errCode, errDetails } = response.data;
            if (success) {
                setLoading(false);
                navigate('/confirm-password-change', {
                    state: {
                        iso2,
                        phone,
                    },
                });
            } else if (!success && errCode) {
                console.error(errDetails);
                setLoading(false);
                setFormError(errCode);
            }
        } catch (e) {
            console.error(e);
            if (e.code) setFormError(e.code);
        }
    };

    return (
        countries &&
        countries.length > 0 && (
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <div className={classes.paper}>
                    <Logo className={classes.logo} />
                    <Typography component="h1" variant="h5">
                        Change password
                    </Typography>
                    <Formik
                        initialValues={{
                            iso2: iso2,
                            phone: phone,
                            password: password,
                            passwordConfirmation: passwordConfirmation,
                        }}
                        onSubmit={ChangePassword}
                        validationSchema={Schema}
                        validateOnBlur
                        validateOnChange
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                        }) => (
                            <Form className={classes.form} noValidate>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    error={!!(errors.iso2 && touched.iso2)}
                                >
                                    <InputLabel id="country-label" shrink>
                                        Country
                                    </InputLabel>
                                    <Select
                                        name="iso2"
                                        value={values.iso2}
                                        disabled={loading}
                                        error={errors.iso2 && touched.iso2}
                                        fullWidth
                                        id="iso2"
                                        labelId="country-label"
                                        onBlur={handleBlur}
                                        onChange={(value) => {
                                            formError && setFormError(null);
                                            handleChange(value);
                                        }}
                                        label="Country"
                                    >
                                        {countries.map((c) => (
                                            <MenuItem
                                                key={c.Iso2}
                                                value={c.Iso2}
                                            >
                                                {c.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.iso2 && touched.iso2 && (
                                        <FormHelperText>
                                            {errors.iso2}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                <Field
                                    autoComplete="tel-national"
                                    component={TextField}
                                    value={values.phone}
                                    disabled={loading}
                                    error={errors.phone && touched.phone}
                                    fullWidth
                                    helperText={
                                        errors.phone && touched.phone
                                            ? errors.phone
                                            : ''
                                    }
                                    id="phone"
                                    label="Phone"
                                    margin="normal"
                                    name="phone"
                                    onBlur={handleBlur}
                                    onChange={(event) => {
                                        formError && setFormError(null);
                                        const numericValue =
                                            event.target.value.replace(
                                                /[^\d]/g,
                                                ''
                                            );
                                        handleChange({
                                            target: {
                                                name: 'phone',
                                                value: numericValue,
                                            },
                                        });
                                    }}
                                    size="small"
                                    type="tel"
                                    variant="outlined"
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                    }}
                                />
                                <Field
                                    component={TextField}
                                    value={values.password}
                                    disabled={loading}
                                    error={errors.password && touched.password}
                                    fullWidth
                                    helperText={
                                        errors.password && touched.password
                                            ? errors.password
                                            : ''
                                    }
                                    id="password"
                                    label="New Password"
                                    margin="normal"
                                    name="password"
                                    onBlur={handleBlur}
                                    onChange={(value) => {
                                        formError && setFormError(null);
                                        handleChange(value);
                                    }}
                                    size="small"
                                    type="password"
                                    variant="outlined"
                                />
                                <Field
                                    component={TextField}
                                    value={values.passwordConfirmation}
                                    disabled={loading}
                                    error={
                                        errors.passwordConfirmation &&
                                        touched.passwordConfirmation
                                    }
                                    fullWidth
                                    helperText={
                                        errors.passwordConfirmation &&
                                        touched.passwordConfirmation
                                            ? errors.passwordConfirmation
                                            : ''
                                    }
                                    id="passwordConfirmation"
                                    label="Repeat Password"
                                    margin="normal"
                                    name="passwordConfirmation"
                                    onBlur={handleBlur}
                                    onChange={(value) => {
                                        formError && setFormError(null);
                                        handleChange(value);
                                    }}
                                    size="small"
                                    type="password"
                                    variant="outlined"
                                />
                                {formError && (
                                    <Typography
                                        align="center"
                                        className={classes.errorMessage}
                                        color="error"
                                        variant="body1"
                                    >
                                        Incorrect login details!
                                    </Typography>
                                )}
                                <div className={classes.wrapper}>
                                    <Button
                                        color="primary"
                                        disabled={loading}
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                    >
                                        Submit
                                    </Button>
                                    {loading && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </div>
                                <Link to="/login">
                                    <Typography
                                        align="center"
                                        variant="body1"
                                        className={classes.notification}
                                    >
                                        Back to sign in
                                    </Typography>
                                </Link>
                            </Form>
                        )}
                    </Formik>
                </div>
            </Container>
        )
    );
}

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        color: 'primary',
        left: '50%',
        marginLeft: -12,
        marginTop: -12,
        position: 'absolute',
        top: '50%',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
    },
    form: {
        marginTop: theme.spacing(1),
        width: '100%', // Fix IE 11 issue.
    },
    logo: {
        marginBottom: theme.spacing(8),
        color: theme.palette.primary.main,
    },
    paper: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(8),
    },
    wrapper: {
        margin: theme.spacing(3, 0, 2),
        position: 'relative',
    },
    notification: {
        color: '#7C87FF',
    },
}));
